import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  ButtonBase,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  InputBase,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers';
import _ from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { bedStatusAPI, eadmissionAPI } from '../../../../../apis';
import { AutoCompleteInput, AutoCompleteSelect, KeyboardTimeInput } from '../../../../../components';
import { AddIcon, CloseIcon, DateIcon, NoticeIcon, AcceptIcon } from '../../../../../components/Icon';
import ElderlyHomeInfo from '../../../../../components/ElderlyHomeInfo';
import { i18n } from '../../../../../language';
import { classNames, initValue } from '../../../../../utils';
import { checkSubmitData } from '../../../../../utils/check';
import MdroInfo from '../../../../../components/MdroInfo';
import Text from '../../../../../components/Text';
import MyAlert from '../../../../../components/Alert';
import { alertMsg } from '../../../../../apis/commlogic';
import { hospitalWardListFunc } from '../../../../../utils/hospitalWardList';
import AutoCompleteMultiCheckbox2 from '../../../../../components/AutoCompleteMultiCheckbox2';
import { getTransferInOutAdmissionTypeList, isNotEmptyArray } from '../../../../../utils/newBedStatusUtil';
import AutoCompleteInput2 from '../../../../../components/AutoCompleteInput2';
import CmsAlert, { CMS_ALERT_ICON_VARIANT } from '../../../../../components/CmsAlert';
import { bedP } from '../../../../BedStatus/bsApp';
import useDiagnosisSearch from './useDiagnosisSearch';
import useTransferInAlert from './useTransferInAlert';
import {
  P,
  alertText,
  buildInitFormData,
  buildPatientRetrieveFormData,
  buildTransferInSchema,
  buildTransferInSubmitData,
  hasPatientRetrieveData,
  parseIntegerInput,
  saveBtnDisabled,
  validateBlurField,
  validateDateField,
  validatePatientRetrieveFormData,
  validateRetrievePatientIdentity,
} from './helpers';
import {
  AddTextInput,
  baseCssBooking,
  baseCssOccupieddischarge,
  bsAttentionFunc,
  bsMlookupListFunc,
} from './legacyBedStatusAdapter';

// TODO TransferIn cleanup: 当前 popup 仍临时复用 BedStatus legacy 依赖。
// 在 e-admission Transfer In 的保存和显示契约确认后，再逐步替换为本模块实现。
const DRAFT_DESTINATION_ALERT_TEXT =
  'As destination info is missing, this request is not yet sent to ward. This is a draft only.';

// 判断 destination 信息有没有填完整。
const hasMissingDestinationInfo = (formData) => {
  return !formData[P.destHospCode] || !formData[P.destWardId] || !formData[P.destWardLocation];
};

// 缺少默认医院时补一个空ward选项，确保destination级联下拉仍能正常显示。
const appendMissingDestHospital = (hospitalList, hospitalCode) => {
  if (!hospitalCode || _.some(hospitalList, (item) => item?.value === hospitalCode)) {
    return hospitalList;
  }

  return [
    ...hospitalList,
    {
      hospCode: hospitalCode,
      text: hospitalCode,
      value: hospitalCode,
      wards: [],
    },
  ];
};

const useStyles = makeStyles((theme) => ({
  chineseNameInput: {
    '& .MuiInputBase-input': {
      fontFamily: 'HA_MingLiu, NotoSans-Medium, sans-serif !important',
    },
  },
  textField: {
    borderRadius: '0.625rem',
    marginBottom: '0.571rem',
    border: 'solid 1px #e4e7eb',
    padding: '0 1rem',
    backgroundColor: '#fff',
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
      lineHeight: '1.8rem',
    },
    '& :before': {
      content: 'none',
    },
    '& :after': {
      content: 'none',
    },
  },
  dialog: {
    '& .MuiPaper-root': {
      // width: '80%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      background: 'transparent',
      boxShadow: 'unset',
    },
  },
  root: {
    width: '100%',
    maxWidth: '100rem',
    display: 'flex',
    borderRadius: '0.625rem',
    boxShadow: '0 -2px 21px 0 rgba(30, 56, 140, 0.18)',
    backgroundColor: '#fff',
    background: '#fff',
    height: '100%',
    overflow: 'auto',
    minWidth: '100%',
    flexDirection: 'column',
  },
  grapText: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#c0c6cb',
  },
  keyboardTimeInput: {
    paddingRight: '0',
    '& .MuiFormControl-root': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  multilineText: {
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    padding: '0 1rem',
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
      lineHeight: '1.8rem',
    },
    '& :before': {
      content: 'none',
    },
    '& :after': {
      content: 'none',
    },
  },
  addIcon: {
    width: '1.125rem',
    height: '1.125rem',
    color: '#538fff',
    marginRight: '0.813rem',
  },
  addRemarks: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#538fff',
  },
  remarks: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#2e4469',
  },
  xing: {
    color: '#ff0300',
    padding: '0 2px',
    fontSize: '1rem',
    lineHeight: '1rem',
  },
  assignedBedNo: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#2e4469',
  },
  assignedBedNoValue: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#7d86a9',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignContent: 'center',
    alignItems: 'center',
    padding: '0 1.53rem',
  },
  remaksText: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.125rem',
    fontWeight: '500',
    color: '#2e4469',
    marginLeft: '0.688rem',
  },
  keyboardDatePicker: {
    paddingRight: '0',
  },
  dateIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  genderBtn: {
    width: '50%',
    height: '3.5rem',
  },
  genderBtnM: {
    width: '50%',
    height: '3.5rem',
  },
  genderBtnF: {
    width: '50%',
    height: '3.5rem',
  },
  gender: {
    flexBasis: 'unset',
  },
  datepicker: {
    '& *': {
      padding: '0',
      margin: '0',
      border: '0',
    },
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      color: '#2e4469',
      height: '3.5rem',
      lineHeight: '3.5rem',
      display: 'flex',
      justifyContent: 'center',
      alignContent: 'center',
      borderRadius: '0.625rem',
      border: 'solid 1px #e4e7eb',
      backgroundColor: '#fff',
      padding: '0 1.25rem',
      fontSize: '1rem',
    },
    '& .MuiInputBase-input': {
      height: '3.5rem',
      lineHeight: '3.5rem',
    },
    '& :focus': {
      backgroundColor: 'unset',
    },
    '& :before': {
      content: 'none',
    },
    ':before': {
      content: 'none !important',
    },
    '& :after': {
      content: 'none',
    },
  },
  labelError: {
    color: '#f44336',
  },
  inputError: {
    border: '1px solid #f44336',
  },
  errorMsg: {
    margin: '3px 0.825rem 0 0.825rem',
    color: '#f44336',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  addTextInput: {
    minHeight: '13.063rem',
    height: 'unset',
  },
  covidDetail: {
    backgroundColor: '#f5faff',
    border: '1px solid #538fff',
    borderRadius: '12px',
    padding: '1rem 1rem 0.75rem',
    margin: '0.688rem  0 2rem 0',
  },
  covidDetailTitle: {
    fontSize: '1rem',
    color: '#1973e6',
    fontFamily: 'NotoSans-Bold',
    fontWeight: 'bold',
    marginBottom: '1rem',
  },
  buttonBase: {
    '&.MuiButtonBase-root.Mui-disabled': {
      backgroundColor: '#c0c6cb',
    },
  },
  buttonRetrieve: {
    '&.MuiButtonBase-root.Mui-disabled': {
      backgroundColor: '#c0c6cb',
      borderColor: '#c0c6cb',
      color: '#fff',
    },
  },
  disabledStyle: {
    '& .MuiInputBase-root': {
      backgroundColor: '#f5f5f5',
    },

    '& .MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#f5f5f5',
    },
    // '& .Mui-disabled': {
    //   backgroundColor: '#f5f5f5',
    // },
  },
  bgYellow: {
    backgroundColor: '#fff3bb',
    borderRadius: '15px',
    padding: '0.5rem 0.857rem',
  },
  message: {
    padding: '1rem',
    borderRadius: '12px',
    color: '#000',
    marginBottom: '2rem',
  },
  messageRed: {
    backgroundColor: '#fff0f0',
  },
  messageYellow: {
    backgroundColor: '#fff3bb',
  },
  messageGreen: {
    backgroundColor: '#e9faf6',
  },
  messageStatus: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '1rem',
    fontFamily: 'NotoSans-Bold',
    fontWeight: 'bold',
  },
  messageStatusRed: {
    color: '#ff0300',
  },
  messageStatusYellow: {
    color: '#c54e17',
  },
  messageStatuGreen: {
    color: '#34c759',
  },
  messageTitle: {
    fontSize: '0.875rem',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
    marginTop: '0.5rem',
  },
  messageText: {
    fontSize: '1rem',
    fontFamily: 'NotoSans-Medium',
    marginTop: '0.5rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  messageIcon: {
    marginRight: '1rem',
  },
  inputBase: {
    '&.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#f5f5f5',
      color: '#3e3e3e',
    },
  },
  destSelect: {
    '& .MuiInputBase-input': {
      lineHeight: 'initial',
    },
  },
  checkbox: {
    '&.Mui-disabled': {
      '& .MuiIconButton-label': {
        backgroundColor: '#f5f5f5',
      },
    },
  },
  caseNoGroup: {
    backgroundColor: '#F5FAFF',
    borderRadius: '16px',
    display: 'flex',
    padding: '0.642rem 0.857rem',
    flex: 1,
    width: '49%',
    gap: '1.21rem',
  },
  retrieveGroup: {
    borderRadius: '16px',
    border: '1px solid #E4E7EB',
    padding: '12px',
  },
  elderlyHomeInfo: {
    marginTop: '0.62rem',
  },
}));

export default function TransferOutPop({
  closePop,
  title,
  open,
  allData,
  saveHandle,
  disabledData,
  sendBtn = false,
  transferOutDetail,
  transferInPopupOptions = {},
  transferInSetting = {},
}) {
  // 基础状态和衍生资料。
  const loginInfo = useSelector((state) => state.loginInfo);
  const {
    bsHospcodeList = [],
    bsAttentionList = [],
    sysHospitalWardList = [],
    bsRemarkList = [],
    bsMlookupList = [],
    cubicleList = [],
    destHospitalList = [],
  } = transferInPopupOptions;
  const [BookingDataFormat, setBookingDataFormat] = useState(() => buildInitFormData(allData, bsMlookupList));
  const tiRequestStatus = allData?.status ?? '';
  const [isDLStatus, setIsDLStatus] = useState(tiRequestStatus === 'DL');
  const schema = useMemo(() => buildTransferInSchema(BookingDataFormat), [BookingDataFormat]);
  const draftSchema = useMemo(
    () => buildTransferInSchema(BookingDataFormat, { allowEmptyDestination: true }),
    [BookingDataFormat]
  );
  const baseClass = baseCssOccupieddischarge();
  const [PatientRetrieve, setPatientRetrieve] = useState(null);
  const patientStatusData = allData ?? PatientRetrieve;
  const statusList = useMemo(() => {
    let newList = [];
    const allStatus = bsMlookupListFunc.getBSBookStatusAddTranslate(bsMlookupList);
    if (allData?.[P.status] && !['P', 'A'].includes(allData?.[P.status]) && BookingDataFormat[P.status] !== 'P') {
      newList = [
        ...allStatus,
        { text: i18n.t('BEDSTATUS_RECV_WARD_READY'), value: 'RWR' },
        { text: 'Draft', value: 'DRAFT' },
      ];
    } else if (allData?.[P.status] === 'DL') {
      newList.push({ text: i18n.t('BEDSTATUS_PENDING'), value: 'P' });
    } else if (allData?.[P.status] === 'A') {
      newList.push(
        { text: i18n.t('BEDSTATUS_ACCEPTED'), value: 'A' },
        { text: i18n.t('BEDSTATUS_DELETED'), value: 'D' }
      );
    } else {
      newList = [
        { text: i18n.t('BEDSTATUS_PENDING'), value: 'P' },
        { text: i18n.t('BEDSTATUS_DELETED'), value: 'D' },
      ];
    }
    return newList;
  }, [bsMlookupList, BookingDataFormat, allData?.[P.status]]);
  const counter = useMemo(() => {
    return Math.round((moment().valueOf() - BookingDataFormat?.[P.covidDate]) / (24 * 60 * 60 * 1000));
  }, [BookingDataFormat[P.covidDate]]);
  const onVentilatorList = useMemo(() => {
    return [
      { text: '\u2002', value: null },
      ...bsMlookupListFunc.getTypeLookupFormat(bsMlookupList, 'bs_book_ventilator'),
    ];
  }, [bsMlookupList]);
  const admissionTypeList = useMemo(() => getTransferInOutAdmissionTypeList(bsMlookupList), [bsMlookupList]);
  const [errorMsg, setErrorMsg] = useState(null);

  // 顶部提示弹窗控制。
  const { closeMyAlert, isMyAlert, showMyAlert } = useTransferInAlert();
  const { diagnosisOptions, handleDiagnosisKeywordChange } = useDiagnosisSearch(loginInfo.wardId);

  // lookup 加载完成后，为新增弹窗补上默认 status。
  useEffect(() => {
    if (!allData && bsMlookupListFunc.isTypeLookupValueExist(bsMlookupList, 'bs_book_status', 'P')) {
      setBookingDataFormat((prev) => {
        if (prev[P.status]) {
          return prev;
        }
        return {
          ...prev,
          [P.status]: 'P',
        };
      });
    }
  }, [allData, bsMlookupList]);

  // 新增弹窗默认值处理。
  useEffect(() => {
    if (sysHospitalWardList && Array.isArray(bsHospcodeList) && !allData) {
      const hospCode = hospitalWardListFunc.getIpasFirstHospitalCode(
        sysHospitalWardList,
        loginInfo.hospitalId,
        loginInfo.wardId
      );
      const defaultHospitalCode = bsHospcodeList.includes(hospCode) ? hospCode : bsHospcodeList[0];
      if (!defaultHospitalCode) {
        return;
      }

      setBookingDataFormat((prev) => ({
        ...prev,
        [P.hospitalCode]: defaultHospitalCode,
        [P.destHospCode]: defaultHospitalCode,
        [P.destWardId]: '',
        [P.destWardLocation]: '',
        // 新增Transfer In时带入setting默认的Dest. Unit和Admission Type。
        [P.ward]: prev[P.ward] || transferInSetting?.defaultFromUnit || '',
        [P.admissionType]: prev[P.admissionType] || transferInSetting?.defaultAdmType || '',
      }));
    }
  }, [
    sysHospitalWardList,
    bsHospcodeList,
    allData,
    loginInfo.hospitalId,
    loginInfo.wardId,
    transferInSetting?.defaultAdmType,
    transferInSetting?.defaultFromUnit,
  ]);
  // 目标 ward 重定向。
  const redirectToOtherWardHandler = useCallback(() => {
    setIsDLStatus(false);
    setBookingDataFormat((prev) => ({
      ...prev,
      [P.destHospCode]: '',
      [P.destWard]: '',
      [P.destWardId]: '',
      [P.wardCd]: '',
      [P.assignedBedNo]: '',
      [P.destSpecialty]: '',
      [P.status]: 'P',
    }));
  }, []);

  // 暂用 BedStatus patient retrieve API，根据 case number、hospital code 或 HKID 查询病人资料。
  const loadPatientRetrieve = useCallback(
    async (caseNo, hospCode, hkid) => {
      try {
        const { result } = await bedStatusAPI.postPatientRetrieve({ caseNo, hospCode, hkid });
        setPatientRetrieve(result);
        // result 为 null 时不会进入下方 PatientRetrieve effect，这里直接显示失败提示。
        result === null && showMyAlert(1);
      } catch {}
    },
    [showMyAlert]
  );

  useEffect(() => {
    (async () => {
      if (PatientRetrieve) {
        const newTemp = buildPatientRetrieveFormData(PatientRetrieve, bsAttentionList);
        if (hasPatientRetrieveData(PatientRetrieve, newTemp)) {
          showMyAlert(2);
        } else {
          showMyAlert(1);
        }
        setBookingDataFormat((prev) => ({
          ...prev,
          ...newTemp,
        }));
        const nextErrorMsg = await validatePatientRetrieveFormData(newTemp);
        setErrorMsg((prev) => ({ ...prev, ...nextErrorMsg }));
      }
    })();
  }, [PatientRetrieve, bsAttentionList, showMyAlert]);

  // 校验病人识别资料后查询病人详情。
  const retrieveClick = useCallback(async () => {
    try {
      await validateRetrievePatientIdentity(BookingDataFormat, schema);
      setErrorMsg((prev) => ({ ...prev, [P.patienID]: null, [P.hkid]: null }));
      await loadPatientRetrieve(
        BookingDataFormat[P.patienID],
        BookingDataFormat[P.hospitalCode],
        BookingDataFormat[P.hkid]
      );
    } catch (e) {
      setErrorMsg((prev) => ({ ...prev, ...e }));
    }
  }, [BookingDataFormat, schema, loadPatientRetrieve]);

  // 表单字段更新。
  const patientKeyVal = useMemo(() => {
    let value = null;
    if (
      allData?.[P.patienID] === BookingDataFormat[P.patienID] &&
      allData?.[P.hospitalCode] === BookingDataFormat[P.hospitalCode]
    ) {
      if (PatientRetrieve?.patientKey) {
        value = PatientRetrieve?.patientKey;
      } else {
        value = allData?.patientKey;
      }
    } else if (PatientRetrieve?.patientKey) {
      value = PatientRetrieve?.patientKey;
    }

    return value;
  }, [
    PatientRetrieve?.patientKey,
    BookingDataFormat[P.patienID],
    BookingDataFormat[P.hospitalCode],
    allData?.[P.patienID],
    allData?.[P.hospitalCode],
    allData?.[P.patientKey],
  ]);

  // 更新表单的局部字段，保持原有合并方式。
  const updateBookingDataFormat = useCallback((nextValue) => {
    setBookingDataFormat((prev) => ({ ...prev, ...nextValue }));
  }, []);

  const onChangeHandle = useCallback(
    (type, key) => async (event) => {
      let temp;
      switch (type) {
        case 'text':
          updateBookingDataFormat({ [key]: event.target.value });
          break;
        case 'up_text':
          updateBookingDataFormat({ [key]: _.toUpper(event.target.value) });
          break;
        case 'select_hosp_code':
          updateBookingDataFormat({ [key]: _.toUpper(event) });
          break;
        case 'select_dest_hospital':
          updateBookingDataFormat({
            [key]: event,
            [P.destWardId]: '',
            [P.destWardLocation]: '',
          });
          break;
        case 'select_dest_unit':
          updateBookingDataFormat({
            [key]: event,
            [P.destWardLocation]: '',
          });
          break;
        case 'text_positive_integer':
          temp = parseIntegerInput(event.target.value);
          if (temp === '' || temp <= 120) {
            updateBookingDataFormat({ [key]: temp });
          }
          break;
        case 'integer':
          temp = parseIntegerInput(event.target.value);
          updateBookingDataFormat({ [key]: temp });
          break;
        case 'select':
          updateBookingDataFormat({ [key]: initValue(event) });
          break;
        case 'date':
          temp = await validateDateField({ key, schema, value: event });
          setErrorMsg((prev) => ({ ...prev, ...temp }));
          updateBookingDataFormat({ [key]: event });
          break;
        case 'check':
          updateBookingDataFormat({ [key]: Number(event.target.checked) });
          break;

        default:
          break;
      }
    },
    [schema, updateBookingDataFormat]
  );

  const onGenderClick = useCallback(
    (key, value) => () => {
      (async () => {
        await onChangeHandle('select', key)(value);
      })();
    },
    [onChangeHandle]
  );

  const destHospitalOptions = useMemo(() => {
    return appendMissingDestHospital(destHospitalList, BookingDataFormat[P.hospitalCode]);
  }, [destHospitalList, BookingDataFormat[P.hospitalCode]]);

  const destUnitOptions = useMemo(() => {
    const destUnitList =
      _.find(destHospitalOptions, (item) => item?.value === BookingDataFormat[P.destHospCode])?.wards || [];
    const revampUnitList = _.map(destUnitList, (item) => ({
      ...item,
      text: item?.wardName,
      value: item?.wardId,
    }));
    return revampUnitList;
  }, [destHospitalOptions, BookingDataFormat[P.destHospCode]]);

  const destWardLocationOptions = useMemo(() => {
    const destWardLocList =
      _.find(destUnitOptions, (item) => item?.value === BookingDataFormat[P.destWardId])?.wardLocations || [];
    return _.map(destWardLocList, (item) => ({
      text: item,
      value: item,
    }));
  }, [destUnitOptions, BookingDataFormat[P.destWardId]]);

  // 只有一个选项的时候，自动选择这个 dest ward location。
  useEffect(() => {
    const onlyLocation = destWardLocationOptions?.[0]?.value;
    if (destWardLocationOptions.length === 1) {
      setBookingDataFormat((prev) => {
        if (prev[P.destWardLocation] === onlyLocation) {
          return prev;
        }
        return {
          ...prev,
          [P.destWardLocation]: initValue(onlyLocation),
        };
      });
    }
  }, [destWardLocationOptions]);

  const destUnitName = useMemo(() => {
    return _.find(destUnitOptions, (item) => item?.value === BookingDataFormat[P.destWardId])?.wardName ?? '';
  }, [destUnitOptions, BookingDataFormat[P.destWardId]]);

  const baseClasses = baseCssBooking();
  const classes = useStyles();

  // 表单校验样式和失焦校验。
  const getCheckLableClasses = (key) => {
    return errorMsg?.[key] ? classNames(baseClasses.labelText, classes.labelError) : baseClasses.labelText;
  };

  const getCheckInputClasses = (key) => {
    return errorMsg?.[key] ? classNames(baseClasses.inputText, classes.inputError) : baseClasses.inputText;
  };

  const focusCheck = useCallback(
    (name) => async () => {
      try {
        const nextErrorMsg = await validateBlurField({ name, schema, BookingDataFormat });
        setErrorMsg((prev) => ({ ...prev, ...nextErrorMsg }));
      } catch (e) {
        setErrorMsg((prev) => ({ ...prev, ...e }));
      }
    },
    [BookingDataFormat, schema]
  );

  // 校验并提交弹窗表单。
  const saveClick = useCallback(
    async (deletedStatus, allowEmptyDestination = false) => {
      try {
        const submitSchema = allowEmptyDestination ? draftSchema : schema;
        await checkSubmitData(Yup.object().shape(submitSchema), BookingDataFormat);
        setErrorMsg(null);
        if (!deletedStatus && hasMissingDestinationInfo(BookingDataFormat)) {
          alertMsg(DRAFT_DESTINATION_ALERT_TEXT, _.uniqueId('transfer_in_draft_'), 'warning', 3000);
        }
        const saveValue = await buildTransferInSubmitData({
          allData,
          BookingDataFormat,
          destUnitName,
          patientKeyVal,
          deletedStatus,
        });
        if (_.isFunction(saveHandle)) {
          saveHandle(saveValue);
        }
      } catch (errorMsg) {
        setErrorMsg(errorMsg);
      }
    },
    [schema, draftSchema, BookingDataFormat, allData, destUnitName, patientKeyVal, saveHandle]
  );

  const deleteTransferRequestHandler = useCallback(() => {
    (async () => {
      await saveClick('D', true);
    })();
  }, [saveClick]);

  const completeTransferRequestHandler = useCallback(() => {
    (async () => {
      await saveClick('C', true);
    })();
  }, [saveClick]);

  return (
    <Dialog open={open} maxWidth={false} className={classes.dialog}>
      {isMyAlert > 0 && (
        <MyAlert open={Boolean(isMyAlert)} text={alertText(isMyAlert)} type="success" onClose={closeMyAlert} />
      )}
      <div className={classes.root}>
        <Box display="flex" className={classes.header}>
          <Box display="flex" alignItems="baseline">
            <Typography className={baseClasses.headerTitle} style={{ padding: '1.3rem 0' }}>
              {title}
            </Typography>
            <Typography className={classes.assignedBedNo} style={{ marginLeft: '4.563rem' }}>
              <span className={classes.xing}>*</span>
              {i18n.t('BEDSTATUS_FIELDS_ARE_REQUIRED')}
            </Typography>
          </Box>
          <IconButton className={baseClasses.closeIcon} onClick={closePop} style={{ marginRight: '-10px' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <main style={{ display: 'flex', overflow: 'auto', flex: '1', gap: '1.5rem', padding: '0 2.8rem' }}>
          <Box display="flex" flexDirection="column" flexBasis="50%" gridGap="0.642rem">
            <Box display="flex" alignItems="flex-start" gridGap="1.25rem">
              <div className={classes.caseNoGroup}>
                <Box width="50%">
                  <Typography
                    className={getCheckLableClasses(P.patienID)}
                    style={{ margin: '0rem 0rem 0.625rem 0', width: '100%' }}
                  >
                    {i18n.t('BEDSTATUS_PATIENT_ID')}
                    {/* <span className={classes.xing}>*</span> */}
                  </Typography>
                  <InputBase
                    id="bed_transferpop_patien"
                    className={classNames(classes.inputBase, getCheckInputClasses(P.patienID))}
                    value={BookingDataFormat[P.patienID]}
                    onChange={(event) => {
                      (async () => {
                        await onChangeHandle('up_text', P.patienID)(event);
                      })();
                    }}
                    style={{ width: '100%' }}
                    onBlur={() => {
                      (async () => {
                        await focusCheck(P.patienID)();
                      })();
                    }}
                    disabled={disabledData?.hospitalNo || isDLStatus}
                  />
                  {errorMsg?.[P.patienID] && (
                    <Typography className={classes.errorMsg}>{errorMsg[P.patienID]}</Typography>
                  )}
                </Box>
                <Box width="50%">
                  <Typography
                    className={getCheckLableClasses(P.hospitalCode)}
                    style={{ margin: '0rem 0 0.625rem 0', width: '100%' }}
                  >
                    {i18n.t('BEDSTATUS_HOSPITAL_CODE')}
                    <span className={classes.xing}>*</span>
                  </Typography>
                  <AutoCompleteInput2
                    id="bed_transferpop_hospitalcode"
                    itemList={_.map(bsHospcodeList, (item) => {
                      return { text: item, value: item };
                    })}
                    value={BookingDataFormat[P.hospitalCode]}
                    onChange={(event) => {
                      (async () => {
                        await onChangeHandle('select_hosp_code', P.hospitalCode)(event);
                      })();
                    }}
                    errorMsg={errorMsg?.[P.hospitalCode]}
                    onBlur={() => {
                      (async () => {
                        await focusCheck(P.hospitalCode)();
                      })();
                    }}
                    disabled={disabledData?.hospitalCode || isDLStatus}
                    freeSolo={true}
                  />
                </Box>
              </div>

              <Box flex={1} pt="0.857rem" display="flex" gridGap="1.21rem">
                <Box width="50%">
                  <Typography
                    className={getCheckLableClasses(P.hkid)}
                    style={{ margin: '0rem 0rem 0.57rem 0', width: '100%' }}
                  >
                    HKID / Doc. No.
                  </Typography>
                  <InputBase
                    id="bed_transferpop_patien"
                    // className={getCheckInputClasses(P.hkid)}
                    className={baseClasses.inputText}
                    value={BookingDataFormat[P.hkid]}
                    onChange={(event) => {
                      (async () => {
                        await onChangeHandle('text', P.hkid)(event);
                      })();
                    }}
                    style={{ width: '100%' }}
                    onBlur={() => {
                      (async () => {
                        await focusCheck(P.hkid)();
                      })();
                    }}
                  />
                  {Boolean(errorMsg?.[P.hkid]) && (
                    <Typography className={classes.errorMsg}>{errorMsg[P.hkid]}</Typography>
                  )}
                </Box>
                <Box width="50%" alignSelf="flex-start" mt="1.882rem">
                  <ButtonBase
                    id="bed_transferpop_retrieve"
                    className={classNames(baseClasses.bottomDeleteBtn, classes.buttonRetrieve)}
                    onClick={() => {
                      (async () => {
                        await retrieveClick();
                      })();
                    }}
                    style={{ height: '3.5rem', width: '100%' }}
                    disabled={disabledData?.retrieve || isDLStatus}
                  >
                    {i18n.t('BEDSTATUS_RETRIEVE')}
                  </ButtonBase>
                </Box>
              </Box>
            </Box>

            <Box display="flex" gridGap="1.25rem">
              <Box width="25%">
                <Typography className={getCheckLableClasses(P.name)} style={{ marginBottom: '0.571rem' }}>
                  Eng Name
                  <span className={classes.xing}>*</span>
                </Typography>
                <InputBase
                  id="bed_transferpop_name"
                  className={classNames(classes.inputBase, getCheckInputClasses(P.name))}
                  value={BookingDataFormat[P.name]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.name)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  onBlur={() => {
                    (async () => {
                      await focusCheck(P.name)();
                    })();
                  }}
                  disabled={disabledData?.name || isDLStatus}
                />
                {errorMsg?.[P.name] && <Typography className={classes.errorMsg}>{errorMsg[P.name]}</Typography>}
              </Box>
              <Box width="25%">
                <Typography className={getCheckLableClasses(P.chiName)} style={{ marginBottom: '0.571rem' }}>
                  Chi. Name
                  {/* <span className={classes.xing}>*</span> */}
                </Typography>
                <InputBase
                  id="bed_transferpop_name"
                  className={classNames(classes.inputBase, getCheckInputClasses(P.chiName), classes.chineseNameInput)}
                  value={BookingDataFormat[P.chiName]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.chiName)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  onBlur={() => {
                    (async () => {
                      await focusCheck(P.chiName)();
                    })();
                  }}
                  disabled={disabledData?.chiName || isDLStatus}
                />
                {errorMsg?.[P.chiName] && <Typography className={classes.errorMsg}>{errorMsg[P.chiName]}</Typography>}
              </Box>
              <Box width="25%">
                <Typography className={getCheckLableClasses(P.age)} style={{ width: '100%', marginBottom: '0.571rem' }}>
                  {i18n.t('BEDSTATUS_AGE')}
                  <span className={classes.xing}>*</span>
                </Typography>
                <InputBase
                  id="bed_transferpop_age"
                  className={classNames(classes.inputBase, getCheckInputClasses(P.age))}
                  value={BookingDataFormat[P.age] ?? ''}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text_positive_integer', P.age)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  onBlur={() => {
                    (async () => {
                      await focusCheck(P.age)();
                    })();
                  }}
                  disabled={disabledData?.age || isDLStatus}
                />
                {errorMsg?.[P.age] && <Typography className={classes.errorMsg}>{errorMsg[P.age]}</Typography>}
              </Box>
              <Box minWidth="25%" maxWidth="11.063rem">
                <Typography
                  className={getCheckLableClasses(P.specialtyCode)}
                  style={{ width: '100%', marginBottom: '0.571rem' }}
                >
                  {i18n.t('BEDSTATUS_SPECIALTY_CODE')}
                  <span className={classes.xing}>*</span>
                </Typography>
                <InputBase
                  id="bed_transferpop_specialty_code"
                  className={classNames(classes.inputBase, baseClasses.inputText)}
                  placeholder={i18n.t('BEDSTATUS_SELECT_SPECIALTY_CODE')}
                  value={BookingDataFormat[P.specialtyCode]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.specialtyCode)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  onBlur={() => {
                    (async () => {
                      await focusCheck(P.specialtyCode)();
                    })();
                  }}
                  disabled={disabledData?.specialtyCode || isDLStatus}
                />
                {errorMsg?.[P.specialtyCode] && (
                  <Typography className={classes.errorMsg}>{errorMsg[P.specialtyCode]}</Typography>
                )}
              </Box>
            </Box>

            <Box display="flex" gridGap="1.25rem">
              <Box width="25%">
                <Typography className={getCheckLableClasses(P.gender)} style={{ marginBottom: '0.571rem' }}>
                  {i18n.t('BEDSTATUS_GENDER')}
                  <span className={classes.xing}>*</span>
                </Typography>
                <div>
                  <div className={classNames(baseClasses.gender, classes.gender)}>
                    <ButtonBase
                      id="bed_transferpop_male"
                      className={
                        BookingDataFormat[P.gender] === 'M'
                          ? classNames(baseClasses.genderBtn, baseClasses.selectGender, classes.genderBtnM)
                          : classNames(baseClasses.genderBtn, classes.genderBtn)
                      }
                      onClick={onGenderClick(P.gender, 'M')}
                      style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0' }}
                      disabled={disabledData?.gender || isDLStatus}
                    >
                      {i18n.t('BEDSTATUS_MALE')}
                    </ButtonBase>
                    <ButtonBase
                      id="bed_transferpop_female"
                      className={
                        BookingDataFormat[P.gender] === 'F'
                          ? classNames(baseClasses.genderBtn, baseClasses.selectGender, classes.genderBtnF)
                          : classNames(baseClasses.genderBtn, classes.genderBtn)
                      }
                      onClick={onGenderClick(P.gender, 'F')}
                      style={{ borderTopLeftRadius: '0', borderBottomLeftRadius: '0' }}
                      disabled={disabledData?.gender || isDLStatus}
                    >
                      {i18n.t('BEDSTATUS_FEMALE')}
                    </ButtonBase>
                  </div>
                  {errorMsg?.[P.gender] && <Typography className={classes.errorMsg}>{errorMsg[P.gender]}</Typography>}
                </div>
              </Box>
              <Box width="25%">
                <Typography className={baseClasses.labelText} style={{ width: '100%', marginBottom: '0.571rem' }}>
                  Loc.
                </Typography>
                <AutoCompleteInput
                  id="admission_transferpop_loc"
                  itemList={cubicleList}
                  placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
                  value={BookingDataFormat[P.cubicleId]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('select', P.cubicleId)(event);
                    })();
                  }}
                  errorMsg={errorMsg?.[P.cubicleId]}
                  onBlur={() => {
                    (async () => {
                      await focusCheck(P.cubicleId)();
                    })();
                  }}
                  className={classes.destSelect}
                  disabled={isDLStatus}
                />
              </Box>
              <Box width="25%">
                <Typography className={baseClasses.labelText} style={{ width: '100%', marginBottom: '0.571rem' }}>
                  {i18n.t('BEDSTATUS_TI_UNIT')}
                </Typography>
                <InputBase
                  id="bed_transferpop_ward"
                  className={classNames(classes.inputBase, baseClasses.inputText)}
                  value={BookingDataFormat[P.ward]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.ward)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  disabled={disabledData?.ward || isDLStatus}
                />
              </Box>
              <Box width="25%">
                <Typography className={baseClasses.labelText} style={{ width: '100%', marginBottom: '0.571rem' }}>
                  {i18n.t('BEDSTATUS_BED_NO')}
                </Typography>
                <InputBase
                  id="bed_transferpop_bed_no"
                  className={classNames(classes.inputBase, baseClasses.inputText)}
                  value={BookingDataFormat[P.bedNo]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.bedNo)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  disabled={disabledData?.bedNo || isDLStatus}
                />
              </Box>
            </Box>

            <Box>
              <Box display="flex" mb="0.57rem">
                <Box width="50%" marginRight="1.25rem">
                  <Typography className={getCheckLableClasses(P.date)}>
                    {i18n.t('BEDSTATUS_DATE')}
                    <span className={classes.xing}>*</span>
                  </Typography>
                </Box>
                <Box width="50%">
                  <Typography className={getCheckLableClasses(P.time)}>
                    {i18n.t('BEDSTATUS_TIME')}
                    <span className={classes.xing}>*</span>
                  </Typography>
                </Box>
              </Box>

              <Box display="flex" alignItems="center">
                <Box width="50%" marginRight="1.25rem">
                  <KeyboardDatePicker
                    id="bed_transferpop_date"
                    autoOk
                    variant="inline"
                    inputVariant="outlined"
                    invalidDateMessage={null}
                    format="DD-MM-yyyy"
                    value={BookingDataFormat[P.date]}
                    className={classNames(
                      classes.datepicker,
                      classes.keyboardDatePicker,
                      (allData?.status === 'PFYA' || isDLStatus) && classes.disabledStyle
                    )}
                    keyboardIcon={
                      <div className={classNames(baseClasses.dateIcon, classes.dateIcon)}>
                        <DateIcon />
                      </div>
                    }
                    onChange={(event) => {
                      (async () => {
                        await onChangeHandle('date', P.date)(event);
                      })();
                    }}
                    placeholder={i18n.t('BEDSTATUS_TIME')}
                    disabled={allData?.status === 'PFYA' || isDLStatus}
                    style={{ width: '100%' }}
                  />
                  {!moment(BookingDataFormat[P.date]).isValid() && errorMsg?.[P.date] && (
                    <Typography className={classes.errorMsg}>{errorMsg[P.date]}</Typography>
                  )}
                </Box>
                <Box width="50%">
                  <KeyboardTimeInput
                    id="bed_transferpop_time"
                    className={classNames(classes.keyboardTimeInput)}
                    value={BookingDataFormat[P.time]}
                    placeholder={i18n.t('BEDSTATUS_SELECT_TIME')}
                    onChange={(event) => {
                      (async () => {
                        await onChangeHandle('date', P.time)(event);
                      })();
                    }}
                    disabled={allData?.status === 'PFYA' || isDLStatus}
                  />
                  {!moment(BookingDataFormat[P.time]).isValid() && errorMsg?.[P.time] && (
                    <Typography className={classes.errorMsg}>{errorMsg[P.time]}</Typography>
                  )}
                </Box>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gridGap="0.5rem" className={classes.bgYellow}>
              <Box>
                <Box display="flex" gridGap="1.25rem" alignItems="flex-end">
                  <Box width="25%">
                    <Typography
                      className={getCheckLableClasses(P.destHospCode)}
                      style={{ margin: '0 0 0.571rem 0', width: '100%' }}
                    >
                      {i18n.t('BEDSTATUS_DEST_HOSPITAL')}
                      <span className={classes.xing}>*</span>
                    </Typography>
                  </Box>
                  <Box width="25%">
                    <Typography
                      className={getCheckLableClasses(P.destWardId)}
                      style={{ margin: '0 0 0.571rem 0', width: '100%' }}
                    >
                      {i18n.t('BEDSTATUS_DEST_WARD')}
                      <span className={classes.xing}>*</span>
                    </Typography>
                  </Box>
                  <Box width="25%">
                    <Typography
                      className={getCheckLableClasses(P.destWardLocation)}
                      style={{ margin: '0 0 0.571rem 0', width: '100%' }}
                    >
                      Dest. Ward Location
                      <span className={classes.xing}>*</span>
                    </Typography>
                  </Box>
                  <Box width="25%">
                    <Typography
                      className={getCheckLableClasses(P.admissionType)}
                      style={{ margin: '0 0 0.571rem 0', width: '100%' }}
                    >
                      {i18n.t('BEDSTATUS_ADMISSION_TYPE')}
                      <span className={classes.xing}>*</span>
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" gridGap="1.25rem">
                  <Box width="25%">
                    <AutoCompleteInput
                      id="bed_transferpop_dest_hospital"
                      itemList={destHospitalOptions}
                      placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
                      value={BookingDataFormat[P.destHospCode]}
                      onChange={(event) => {
                        (async () => {
                          await onChangeHandle('select_dest_hospital', P.destHospCode)(event);
                        })();
                      }}
                      errorMsg={errorMsg?.[P.destHospCode]}
                      onBlur={() => {
                        (async () => {
                          await focusCheck(P.destHospCode)();
                        })();
                      }}
                      className={classes.destSelect}
                      disabled={isDLStatus}
                    />
                  </Box>
                  <Box width="25%">
                    <AutoCompleteInput
                      id="bed_transferpop_dest_ward"
                      itemList={destUnitOptions}
                      placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
                      value={BookingDataFormat[P.destWardId]}
                      onChange={(event) => {
                        (async () => {
                          await onChangeHandle('select_dest_unit', P.destWardId)(event);
                        })();
                      }}
                      errorMsg={errorMsg?.[P.destWardId]}
                      onBlur={() => {
                        (async () => {
                          await focusCheck(P.destWardId)();
                        })();
                      }}
                      className={classes.destSelect}
                      disabled={isDLStatus}
                    />
                  </Box>
                  <Box width="25%">
                    <AutoCompleteInput
                      id="bed_transferpop_dest_ward_location"
                      itemList={destWardLocationOptions}
                      placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
                      value={BookingDataFormat[P.destWardLocation]}
                      onChange={(event) => {
                        (async () => {
                          await onChangeHandle('select', P.destWardLocation)(event);
                        })();
                      }}
                      errorMsg={errorMsg?.[P.destWardLocation]}
                      onBlur={() => {
                        (async () => {
                          await focusCheck(P.destWardLocation)();
                        })();
                      }}
                      disabled={isDLStatus}
                    />
                  </Box>
                  <Box width="25%">
                    <AutoCompleteSelect
                      id="bed_transferpop_admission_type"
                      itemList={admissionTypeList}
                      placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
                      value={BookingDataFormat[P.admissionType]}
                      onChange={(event) => {
                        (async () => {
                          await onChangeHandle('select', P.admissionType)(event);
                        })();
                      }}
                      errorMsg={errorMsg?.[P.admissionType]}
                      onBlur={() => {
                        (async () => {
                          await focusCheck(P.admissionType)();
                        })();
                      }}
                      disabled={isDLStatus}
                      className={classes.destSelect}
                    />
                  </Box>
                </Box>
              </Box>
              <Box>
                <Typography
                  style={{ margin: '0 0 0.571rem 0' }}
                  className={getCheckLableClasses(P.principleDiagnosisText)}
                >
                  {i18n.t('BEDSTATUS_PRINCIPLE_DIAGNOSIS')}
                  {/* <span className={classes.xing}>*</span> */}
                </Typography>
                <Box>
                  <TextField
                    id={P.principleDiagnosisText}
                    className={
                      errorMsg?.[P.principleDiagnosisText]
                        ? classNames(classes.textField, classes.inputError)
                        : classes.textField
                    }
                    multiline={true}
                    fullWidth={true}
                    rows={3}
                    value={BookingDataFormat?.[P.principleDiagnosisText]}
                    onChange={(event) => {
                      (async () => {
                        await onChangeHandle('text', P.principleDiagnosisText)(event);
                      })();
                    }}
                    style={{ backgroundColor: isDLStatus ? '#f5f5f5' : '' }}
                    disabled={isDLStatus}
                    onBlur={() => {
                      (async () => {
                        await focusCheck(P.principleDiagnosisText)();
                      })();
                    }}
                  />
                  {errorMsg?.[P.principleDiagnosisText] && (
                    <Typography className={classes.errorMsg}>{errorMsg[P.principleDiagnosisText]}</Typography>
                  )}
                </Box>
                <AutoCompleteMultiCheckbox2
                  // dropDownIcon={true}
                  align="right"
                  height="3.5rem"
                  width="100%"
                  errMsgMargin="3px 0 0 0.825rem"
                  errMsgWidth="11rem"
                  placeholder={i18n.t('BEDSTATUS_PRINCIPLE_DIAGNOSIS_PLACEHOLDER')}
                  itemList={diagnosisOptions}
                  open={BookingDataFormat[P.principleDiagnosisInputValue]?.length > 2}
                  inputValue={BookingDataFormat[P.principleDiagnosisInputValue]}
                  onChangeInputValue={(event) => {
                    (async () => {
                      await onChangeHandle('select', P.principleDiagnosisInputValue)(event);
                    })();
                    handleDiagnosisKeywordChange(event);
                  }}
                  value={BookingDataFormat[P.principleDiagnosis]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('select', P.principleDiagnosis)(event);
                    })();
                  }}
                  id="bed_transferpop_principle_diagnosis"
                  allText=""
                  onBlur={() => {
                    // 延后处理，避免和 autocomplete 选择事件抢更新顺序。
                    setTimeout(() => {
                      const newText = _.map(BookingDataFormat[P.principleDiagnosis], (item) => item.text);
                      updateBookingDataFormat({
                        [P.principleDiagnosisInputValue]: '',
                        [P.principleDiagnosis]: [],
                        [P.principleDiagnosisText]: [BookingDataFormat[P.principleDiagnosisText], ...newText]
                          .filter(Boolean)
                          .join('\n'),
                      });
                    });
                  }}
                  disabled={isDLStatus}
                />
              </Box>
            </Box>

            <Box display="flex" paddingBottom="1rem" gridGap="1.25rem">
              <Box width="35%">
                <Typography className={baseClasses.labelText} style={{ margin: '0 0 0.571rem 0', width: '100%' }}>
                  {i18n.t('BEDSTATUS_DEST_SPECIALTY')}
                </Typography>

                <InputBase
                  id="bed_transferpop_bed_no"
                  className={classNames(baseClasses.inputText, classes.inputBase)}
                  value={BookingDataFormat[P.destSpecialty]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.destSpecialty)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  disabled={isDLStatus}
                />
              </Box>

              <Box width="35%">
                <Typography
                  className={getCheckLableClasses(P.wardCd)}
                  style={{ margin: '0 0 0.571rem 0', width: '100%' }}
                >
                  {i18n.t('BEDSTATUS_ASSIGNED_WARD_CODE')}
                </Typography>
                <InputBase
                  id="bed_transferpop_admission_ward_code"
                  className={classNames(classes.inputBase, baseClasses.inputText)}
                  value={BookingDataFormat[P.wardCd]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.wardCd)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  disabled={true}
                />
              </Box>
              <Box minWidth="30%" maxWidth="11.063rem">
                <Typography
                  style={{ margin: '0 0 0.571rem 0', width: '100%' }}
                  className={getCheckLableClasses(P.assignedBedNo)}
                >
                  {i18n.t('BEDSTATUS_ASSIGNED_BED_NO')}
                </Typography>
                <InputBase
                  id="bed_transferpop_assigned_bed_dno"
                  className={classNames(classes.inputBase, baseClasses.inputText)}
                  value={BookingDataFormat[P.assignedBedNo]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.assignedBedNo)(event);
                    })();
                  }}
                  style={{ width: '100%' }}
                  disabled={true}
                />
              </Box>
            </Box>
          </Box>
          <div style={{ marginBottom: '1.625rem', width: '1px', background: '#e4e7eb' }} />
          <Box display="flex" flexDirection="column" flexBasis="50%">
            <Box mt="0.5rem">
              <Box display="flex" alignItems="center">
                <Typography className={baseClasses.rightLabel} style={{ marginRight: '0.5rem', color: '#7d86a9' }}>
                  {i18n.t('BEDSTATUS_ON_VENTILATOR')}
                </Typography>
                <AutoCompleteSelect
                  id="bed_transferpop_on_ventilator"
                  itemList={onVentilatorList}
                  placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEM')}
                  value={BookingDataFormat[P.onVentilator]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('select', P.onVentilator)(event);
                    })();
                  }}
                  disabled={isDLStatus}
                />
                <TextField
                  id="bed_transferpop_eg7"
                  variant="outlined"
                  className={classNames(baseClasses.inputText, baseClasses.eg7)}
                  InputProps={{
                    endAdornment: <Typography className={baseClasses.LIcon}>%</Typography>,
                  }}
                  placeholder="eg. 7"
                  value={BookingDataFormat[P.egPercent]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.egPercent)(event);
                    })();
                  }}
                  style={{ width: '40%', marginLeft: '0.5rem', backgroundColor: isDLStatus ? '#f5f5f5' : '' }}
                  type="number"
                  step="0.1"
                  disabled={isDLStatus}
                />
              </Box>
            </Box>
            <div>
              <Box display="flex" m="0.5rem 0 0.5rem 0" alignItems="center">
                <FormControlLabel
                  id="bed_transferpop_on_oxygen"
                  control={<Checkbox disabled={isDLStatus} className={classes.checkbox} />}
                  label={i18n.t('BEDSTATUS_ON_OXYGEN')}
                  className={classNames(baseClasses.checkBox)}
                  checked={Boolean(BookingDataFormat[P.onOxygen])}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('check', P.onOxygen)(event);
                    })();
                  }}
                />
                <TextField
                  id="bed_transferpop_egL"
                  variant="outlined"
                  className={classNames(baseClasses.inputText, baseClasses.eg7)}
                  InputProps={{
                    endAdornment: <Typography className={baseClasses.LIcon}>L</Typography>,
                  }}
                  placeholder="eg. 7"
                  disabled={!BookingDataFormat.onOxygen}
                  value={BookingDataFormat[P.egL]}
                  onChange={(event) => {
                    (async () => {
                      await onChangeHandle('text', P.egL)(event);
                    })();
                  }}
                  style={{ width: '30%', marginRight: '0.5rem', backgroundColor: isDLStatus ? '#f5f5f5' : '' }}
                  type="number"
                  step="0.1"
                />
                <div style={{ marginLeft: '1.5rem' }}>
                  <FormControlLabel
                    id="bed_transferpop_tracheotomy"
                    control={<Checkbox disabled={isDLStatus} className={classes.checkbox} />}
                    label={i18n.t('BEDSTATUS_TRACHEOTOMY')}
                    className={baseClasses.checkBox}
                    checked={Boolean(BookingDataFormat[P.tracheotomy])}
                    onChange={(event) => {
                      (async () => {
                        await onChangeHandle('check', P.tracheotomy)(event);
                      })();
                    }}
                    disabled={isDLStatus}
                  />
                </div>
              </Box>
            </div>
            <div>
              <Box display="flex" m="0.15rem 0 0 0" justifyContent="space-between">
                <Box width="100%">
                  <AddTextInput
                    btnId="bed_transferpop_attention_btn"
                    textId="bed_transferpop_attention_input"
                    popId="bed_transferpop_attention_pop"
                    allData={bsAttentionFunc.getNameList(bsAttentionList)}
                    title={
                      <Typography className={classes.remarks} style={{ margin: '0rem 0 0.575rem 0', color: '#7d86a9' }}>
                        {i18n.t('BEDSTATUS_ATTENTION')}
                      </Typography>
                    }
                    btnText={
                      <Typography className={classes.addRemarks}>
                        <AddIcon className={classes.addIcon} />
                        {i18n.t('BEDSTATUS_ADD_ATTENTION')}
                      </Typography>
                    }
                    onChange={(event) => {
                      (async () => {
                        await onChangeHandle('select', P.attention)(event);
                      })();
                    }}
                    value={BookingDataFormat[P.attention]}
                    popClassName={classes.addTextInput}
                    rows={6}
                    errorMsg={errorMsg?.[P.attention]}
                    onBlur={() => {
                      (async () => {
                        await focusCheck(P.attention)();
                      })();
                    }}
                    disabled={isDLStatus}
                  />
                </Box>
              </Box>
            </div>
            {Boolean(BookingDataFormat[P.covid19]) && (
              <Box width="100%">
                <div className={classes.covidDetail}>
                  <Box display="flex" flexDirection="column">
                    <Typography className={classes.covidDetailTitle}>{i18n.t('BEDSTATUS_COVID_19_DETAIL')}</Typography>
                    <Box display="flex">
                      <Box width="50%" mr="1rem">
                        <Typography className={baseClass.labelText} style={{ marginBottom: '0.25rem' }}>
                          {i18n.t('BEDSTATUS_DETECTED_DATE')} <span className={classes.xing}>*</span>
                        </Typography>
                        <Box>
                          <KeyboardDatePicker
                            id="bed_transferpop_date"
                            autoOk
                            variant="inline"
                            inputVariant="outlined"
                            invalidDateMessage={null}
                            format="DD-MM-YYYY"
                            value={BookingDataFormat[P.covidDate]}
                            className={classNames(classes.datepicker, classes.keyboardDatePicker)}
                            keyboardIcon={
                              <div className={classNames(baseClasses.dateIcon, classes.dateIcon)}>
                                <DateIcon />
                              </div>
                            }
                            onChange={(event) => {
                              (async () => {
                                await onChangeHandle('date', P.covidDate)(event);
                              })();
                            }}
                            placeholder={i18n.t('BEDSTATUS_DATE')}
                            disableFuture={true}
                          />
                        </Box>
                      </Box>
                      <Box width="50%">
                        <Typography className={baseClass.labelText} style={{ marginBottom: '0.25rem' }}>
                          {i18n.t('BEDSTATUS_COUNTER_TRANSFER')}
                        </Typography>
                        <TextField
                          id="bed_transferpop_counter"
                          variant="outlined"
                          className={classNames(baseClasses.inputText, baseClasses.eg7)}
                          InputProps={{
                            endAdornment: (
                              <Typography className={baseClasses.LIcon} style={{ color: '#2e4469' }}>
                                {i18n.t('BEDSTATUS_DAY')}
                              </Typography>
                            ),
                          }}
                          style={{ width: '100%', backgroundColor: '#f5f5f5', color: '#2e4469' }}
                          disabled={true}
                          placeholder=""
                          value={counter}
                          onChange={(event) => {
                            (async () => {
                              await onChangeHandle('text', P.counter)(event);
                            })();
                          }}
                          type="number"
                          step="0.1"
                        />
                      </Box>
                    </Box>
                  </Box>
                </div>
              </Box>
            )}
            <AddTextInput
              btnId="bed_transferpop_remarks_btn"
              textId="bed_transferpop_remarks_input"
              popId="bed_transferpop_remarks_pop"
              allData={bsRemarkList}
              title={
                <Typography className={classes.remarks} style={{ margin: '0rem 0 0.438rem 0', color: '#7d86a9' }}>
                  {i18n.t('BEDSTATUS_REMARKS')}
                </Typography>
              }
              btnText={
                <Typography className={classes.addRemarks}>
                  <AddIcon className={classes.addIcon} />
                  {i18n.t('BEDSTATUS_ADD_REMARKS')}
                </Typography>
              }
              value={BookingDataFormat[P.remarks]}
              onChange={(event) => {
                (async () => {
                  await onChangeHandle('select', P.remarks)(event);
                })();
              }}
              style={{ marginTop: '.875rem' }}
              rows={3}
              errorMsg={errorMsg?.[P.remarks]}
              onBlur={() => {
                (async () => {
                  await focusCheck(P.remarks)();
                })();
              }}
              disabled={isDLStatus}
            />
            <Box display="flex" gridGap="0.5rem" m="0.5rem 0">
              <Box width="50%">
                <Text color="#7d86a9" fontSize="1rem">
                  MDRO Alert / CMS Alert
                </Text>
                {isNotEmptyArray(patientStatusData?.[bedP?.mdroInfo]) ||
                isNotEmptyArray(patientStatusData?.[bedP?.cmsAlertInfos]) ? (
                  <>
                    <Box display="flex" flexWrap="wrap" mt="0.62rem" gridGap="0.62rem">
                      {patientStatusData?.[bedP?.mdroInfo]?.map((item) => (
                        <MdroInfo content={item.alertDesc} key={item.alertDesc} popValue={item} />
                      ))}
                    </Box>
                    <CmsAlert
                      alertsData={patientStatusData?.[bedP?.cmsAlertInfos]}
                      variant={CMS_ALERT_ICON_VARIANT.WIDE}
                    />
                  </>
                ) : (
                  '--'
                )}
              </Box>
              <Box width="50%">
                <Text color="#7d86a9" fontSize="1rem">
                  {i18n.t('ELDERLY_HOME')}
                </Text>
                <ElderlyHomeInfo
                  containerClass={classes.elderlyHomeInfo}
                  ehAddrInfo={patientStatusData?.[bedP?.elderlyHome]}
                  emptyIcon="--"
                />
              </Box>
            </Box>
            <Box width="100%" marginRight="1.25rem" paddingBottom="0.5rem">
              <Typography className={getCheckLableClasses(P.status)} style={{ margin: '0rem 0 0.438rem 0' }}>
                {i18n.t('BEDSTATUS_STATUS')} <span className={classes.xing}>*</span>
              </Typography>
              <AutoCompleteSelect
                id="bed_transferpop_status"
                itemList={statusList}
                placeholder={i18n.t('BEDSTATUS_SELECT_FROM_BELOW_ITEMS')}
                value={BookingDataFormat[P.status]}
                onChange={(event) => {
                  (async () => {
                    await onChangeHandle('select', P.status)(event);
                  })();
                }}
                errorMsg={errorMsg?.[P.status]}
                onBlur={() => {
                  (async () => {
                    await focusCheck(P.status)();
                  })();
                }}
                disabled={!allData?.status || ['PFYA', 'DL', 'D', 'C', 'A', 'RWR', 'DRAFT'].includes(allData?.status)}
              />
            </Box>
            <Box width="50%" />
            {isDLStatus && (
              <Box display="flex" flexDirection="column" className={classNames(classes.message, classes.messageRed)}>
                <Typography className={classNames(classes.messageStatus, classes.messageStatusRed)}>
                  <NoticeIcon className={classes.messageIcon} /> {i18n.t('BEDSTATUS_YOUR_REQUES_IS_DECLINED')}
                </Typography>
                <Typography className={classes.messageTitle}>{i18n.t('BEDSTATUS_DECLINE_REMARKS')}</Typography>
                <Typography className={classes.messageText}>{allData?.declineRemark}</Typography>
              </Box>
            )}
            {tiRequestStatus === 'A' &&
              allData?.targetArriveDtm &&
              allData?.requestDtm &&
              moment(allData?.targetArriveDtm).format('DD-MM-yyyy hh:mm') !==
                moment(allData?.requestDtm).format('DD-MM-yyyy hh:mm') && (
                <Box
                  display="flex"
                  flexDirection="column"
                  className={classNames(classes.message, classes.messageYellow)}
                >
                  <Typography className={classNames(classes.messageStatus, classes.messageStatusYellow)}>
                    <AcceptIcon className={classes.messageIcon} /> {i18n.t('BEDSTATUS_TRANSFER_ACCEPTED')}
                  </Typography>
                  <Typography className={classes.messageTitle}>
                    {i18n.t('BEDSTATUS_CHANGED_ADMISSION_DATE_TIME')}
                  </Typography>
                  <Typography className={classes.messageText}>
                    {moment(allData?.requestDtm).format('DD-MM-yyyy hh:mm a')}
                  </Typography>
                </Box>
              )}
            {tiRequestStatus === 'A' &&
              allData?.targetArriveDtm &&
              allData?.requestDtm &&
              moment(allData?.targetArriveDtm).format('DD-MM-yyyy hh:mm') ===
                moment(allData?.requestDtm).format('DD-MM-yyyy hh:mm') && (
                <Box
                  display="flex"
                  flexDirection="column"
                  className={classNames(classes.message, classes.messageGreen)}
                >
                  <Typography className={classNames(classes.messageStatus, classes.messageStatuGreen)}>
                    <AcceptIcon className={classes.messageIcon} /> {i18n.t('BEDSTATUS_TRANSFER_ACCEPTED')}
                  </Typography>
                  <Typography className={classes.messageTitle}>{i18n.t('BEDSTATUS_ADMISSION_DATE_TIME')}</Typography>
                  <Typography className={classes.messageText}>
                    {moment(allData?.requestDtm).format('DD-MM-yyyy hh:mm a')}
                  </Typography>
                </Box>
              )}
          </Box>
        </main>
        <div style={{ borderTop: '1px solid #e4e7eb' }}>
          <div className={baseClasses.bottom} style={{ marginTop: '0.5rem' }}>
            {isDLStatus ? (
              <>
                <ButtonBase
                  id="bed_transferpop_decline"
                  className={baseClasses.bottomDeleteBtn}
                  onClick={() => closePop()}
                  style={{ marginRight: '1.25rem' }}
                >
                  {i18n.t('BEDSTATUS_CANCEL')}
                </ButtonBase>
                <ButtonBase
                  id="bed_transferpop_accept"
                  className={baseClasses.bottomSaveBtn}
                  onClick={redirectToOtherWardHandler}
                >
                  {i18n.t('BEDSTATUS_REDIRECT_TO_OTHER_WARD')}
                </ButtonBase>
              </>
            ) : (
              <>
                <ButtonBase
                  id="bed_transferpop_cancel"
                  className={baseClasses.bottomDeleteBtn}
                  style={{ marginRight: '1.25rem' }}
                  onClick={() => closePop()}
                >
                  {i18n.t('BEDSTATUS_CANCEL')}
                </ButtonBase>
                <ButtonBase
                  id="bed_transferpop_save"
                  className={classNames(
                    baseClasses.bottomSaveBtn,
                    classes.buttonBase,
                    saveBtnDisabled(allData?.status) && classes.disabledStyle
                  )}
                  onClick={() => {
                    (async () => {
                      await saveClick(undefined, true);
                    })();
                  }}
                  disabled={saveBtnDisabled(allData?.status)}
                >
                  {i18n.t('BEDSTATUS_SEND')}
                </ButtonBase>
              </>
            )}
            {['A', 'DRAFT', 'RWR'].includes(tiRequestStatus) && (
              <ButtonBase
                id="bed_transferpop_Deleted"
                className={baseClasses.bottomDeleteBtn}
                style={{ marginLeft: '1.25rem', borderColor: '#ff7270', color: '#fff', backgroundColor: '#ff7270' }}
                onClick={deleteTransferRequestHandler}
              >
                {i18n.t('BEDSTATUS_DELETED_BTN')}
              </ButtonBase>
            )}
            {['A', 'RWR'].includes(tiRequestStatus) && (
              <ButtonBase
                id="bed_transferpop_Deleted"
                className={baseClasses.bottomDeleteBtn}
                style={{ marginLeft: '1.25rem', borderColor: '#28D2AB', color: '#fff', backgroundColor: '#28D2AB' }}
                onClick={completeTransferRequestHandler}
              >
                {i18n.t('EADMISSION_COMPLETED')}
              </ButtonBase>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
